import { ROLES } from "./Acl";
import { BrandGroup, BrandGroupID } from "./Brand";
import { DealerID } from "./Dealer";
import { DealerLocationID } from "./DealerLocation";
import { PausedCarCheck } from "./PausedCarCheck";
import { Question, QuestionGroup } from "./Question";
import { ReportTemplateID } from "./ReportTemplate";
import { Tag, TagID } from "./Tag";

export type ChecklistID = number;

export type ChecklistTemplateID = number;

export enum CHECKLIST_TYPES {
  Regular = 0,
  Aanname = 1,
  Quality = 2,
  Info = 3,
  ExtraParts = 4,
  Deprecated = 5,
  TestDrive = 6,
  Warranty = 7
}

export interface Checklist {
  created_on: string;
  updated_on: string;
  id: ChecklistID; // Claire Checklist ID
  name: string; // Name of the checklist
  active: boolean; // Is this checklist active
  is_active_for_mechanic: boolean; // Is this checklist active for mechanic
  is_locked: boolean; // Is this checklist locked
  include_in_reports: boolean; // Include this checklist in reports
  is_category_mode: boolean; // Category mode is active for this checklist
  check_in_customer: boolean; // Check-in the customer
  role_id: ROLES; // Role of the user who created this checklist
  checklist_type: CHECKLIST_TYPES; // Type of checklist
  dealer_id: DealerID | null; // Dealer Identifier, If it is not nil, then this checklist is for this specific dealer
  template_id: ChecklistTemplateID | null; // Checklist template ID
  is_deleted: boolean; // Is this checklist deleted
  display_order?: number; // Display order of checklists
  template?: ChecklistTemplate; // Checklist template
  question_groups?: QuestionGroup[]; // List of question groups of this checklist
  tag_ids?: TagID[]; // list of tag ids associated with this checklist
  tags?: Tag[]; // Tags associated with this checklist
  brand_group_ids?: BrandGroupID[]; // List of brand group ids associated with this checklist
  brand_groups?: BrandGroup[]; // Brand groups associated with this checklist
  parent_checklist?: Checklist; // Parent Checklist associated with this checklist
  paused_car_check?: PausedCarCheck; // If not nil, contain the paused car check for this checklist
  questions?: Question[]; // List of questions associated with this checklist
  attached_to?: string[]; // List of dealer names and dealer location names that are using this checklist
  images_count?: number; // Aggregate count of the total number of images taken
  advised_count?: number; // Aggregate count of the total advised items
  advised_sold_count?: number; // Aggregate count of the total advised items sold
  necessary_count?: number; // Aggregate count of the total necessary items
  necessary_sold_count?: number; // Aggregate count of the total necessary items sold
}

export interface ChecklistTemplate {
  created_on: string;
  updated_on: string;
  id: ChecklistTemplateID;
  name: string; // Name of the template
  description: string; // Description of the template
  primary_color: string; // Primary color of the template
  text_color: string; // Text color of the template
  icon_color: string; // Icon color of the template
  price_enabled: boolean; // Price is enabled
  customer_ok_enabled: boolean; // Customer ok is enabled
  ready_enabled: boolean; // Ready is enabled
  snooze_enabled: boolean; // Snooze is enabled
  pin_enabled: boolean; // Pin is enabled
  multi_use_enabled: boolean; // Multi-use is enabled
  include_in_reports: boolean; // Include this checklist in reports
  total_pdf_enabled: boolean; // Total in PDF is enabled
  default_only: boolean; // This is default only template
  cc_default_selected: boolean; // CC Default is selected
  cc_default_selected_nvt: boolean; // CC Default is selected for NVT
  pdf_layout_id: ReportTemplateID | null; // Identifier of the pdf layout
  add_purpose: boolean; // Purpose is added
  show_mechanic_name: boolean; // Mechanic name is visible
  can_skip_km_and_date: boolean; // KM and Date is skipped
  hide_timestamp: boolean; // Timestamp is hidden
  skip_update_appointment_status: boolean; // Appointment status updates are enabled
  is_previewable?: boolean; // This ReportTemplate can be previewed before receiving the binary
}

export interface ChecklistsDealers {
  checklist_id: ChecklistID; // Checklist Identifier
  dealer_id: DealerID; // Dealer Identifier
}

export interface ChecklistDealerLocations {
  checklist_id: ChecklistID; // Checklist Identifier
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  display_order: number; // Display order of the checklist for the dealer location
  checklist?: Checklist; // Checklist
}

export interface ChecklistTemplateShort {
  checklist_template_id: ChecklistTemplateID;
  name: string; // Name of the template
  default_only: boolean; // Default only template
}

export interface ChecklistBrandGroup {
  checklist_id: ChecklistID; // Checklist Identifier
  brand_group_id: BrandGroupID; // Brand Group Identifier
}

export interface ChecklistsTag {
  checklist_id: ChecklistID; // Checklist Identifier
  tag_id: TagID; // Tag Identifier
}
